<template>
    <div class="main-panel">
        <div class="page-container">

            <!-- Page header -->
            <div class="page-header">
                <h1 class="page-title">Credit &amp; Entries</h1>
                <p class="page-subtitle">Manage credit winners, entry winners and Rewards Pass entries for competitions.</p>
            </div>

            <!-- Step 1: Select option -->
            <div class="step-card" :class="{ 'step-card--compact': selectedOption }">
                <div class="step-header">
                    <span class="step-badge">1</span>
                    <span class="step-label">Select an action</span>
                    <span v-if="selectedOption" class="step-summary">{{ optionLabel }}</span>
                    <button v-if="selectedOption" class="step-change" @click="resetOption">Change</button>
                </div>
                <div class="step-body" v-if="!selectedOption">
                    <select class="styled-select" v-model="selectedOption">
                        <option disabled :value="null">Choose an option...</option>
                        <option value="credit-winners">Select Credit Winners</option>
                        <option value="entry-winners">Select Entry Winners</option>
                        <option value="auto-entries">Add Rewards Pass Entry</option>
                    </select>
                </div>
            </div>

            <!-- Step 2: Select competition -->
            <div class="step-card" :class="{ 'step-card--compact': selectedCompetitionId }" v-if="selectedOption">
                <div class="step-header">
                    <span class="step-badge">2</span>
                    <span class="step-label">
                        Select a {{ selectedOption === 'auto-entries' ? 'live' : 'recently ended' }} competition
                    </span>
                    <span v-if="selectedCompetitionId" class="step-summary">{{ competitionLabel }}</span>
                    <button v-if="selectedCompetitionId" class="step-change" @click="resetCompetition">Change</button>
                </div>
                <div class="step-body" v-if="!selectedCompetitionId">
                    <div v-if="loadingCompetitions" class="loading-row">
                        <span class="loading-spinner"></span>
                        Loading competitions...
                    </div>
                    <select v-else class="styled-select" v-model="selectedCompetitionId">
                        <option disabled :value="null">Choose a competition...</option>
                        <template v-if="selectedOption !== 'auto-entries'">
                            <option v-for="comp in endedCompetitions"
                                    :key="comp.competitionId"
                                    :value="comp.competitionId">
                                WSP{{ comp.competitionId }} &mdash; {{ comp.competitionName }}
                            </option>
                        </template>
                        <template v-if="selectedOption === 'auto-entries'">
                            <option v-for="comp in activeCompetitions"
                                    :key="comp.competitionId"
                                    :value="comp.competitionId">
                                WSP{{ comp.competitionId }} &mdash; {{ comp.competitionTitle }}
                            </option>
                        </template>
                    </select>
                </div>
            </div>

            <!-- Step 3: Action content -->
            <div v-if="selectedCompetitionId">

                <!-- Credit Winners -->
                <div v-if="selectedOption === 'credit-winners'" class="content-card">
                    <div class="content-card-header">
                        <span class="card-title">Credit Winners</span>
                        <span class="card-badge">WSP{{ selectedCompetitionId }}</span>
                    </div>
                    <div class="content-card-body">

                        <div class="input-row">
                            <div class="field-group">
                                <label class="field-label" for="creditAmount">Credit Amount (£)</label>
                                <input id="creditAmount"
                                       class="field-input"
                                       type="number"
                                       min="1"
                                       v-model.number="newCreditAmount"
                                       placeholder="e.g. 50" />
                            </div>
                            <div class="field-group">
                                <label class="field-label" for="numberOfUsers">Number of Users</label>
                                <input id="numberOfUsers"
                                       class="field-input"
                                       type="number"
                                       min="1"
                                       v-model.number="newNumberOfUsers"
                                       placeholder="e.g. 3" />
                            </div>
                            <button class="btn-add"
                                    @click="addCreditEntry"
                                    :disabled="!newCreditAmount || !newNumberOfUsers">
                                + Add to List
                            </button>
                        </div>

                        <div v-if="creditEntries.length" class="table-wrap">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th class="col-num">#</th>
                                        <th>Credit Amount</th>
                                        <th>Number of Users</th>
                                        <th class="col-action"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(entry, index) in creditEntries" :key="index">
                                        <td class="col-num muted">{{ index + 1 }}</td>
                                        <td><span class="currency">£{{ entry.creditAmount.toLocaleString() }}</span></td>
                                        <td>{{ entry.numberOfUsers }}</td>
                                        <td class="col-action">
                                            <button class="btn-remove" @click="removeCreditEntry(index)" title="Remove">&#x2715;</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="table-footer">
                                <span class="table-summary">
                                    {{ creditEntries.length }} {{ creditEntries.length === 1 ? 'entry' : 'entries' }}
                                </span>
                                <button class="btn-primary" @click="submitCreditWinners" :disabled="isSubmitting">
                                    <span v-if="!isSubmitting">Add Credit</span>
                                    <span v-else class="btn-spinner"></span>
                                </button>
                            </div>
                        </div>

                        <p v-if="!creditEntries.length && !creditWinnerResults.length" class="empty-hint">Use the form above to add credit entries to the list.</p>

                        <!-- Results panel -->
                        <div v-if="creditWinnerResults.length" class="results-card">
                            <div class="results-header">
                                <span class="results-title">Selected Winners</span>
                                <span class="results-count">{{ creditWinnerResults.length }} user{{ creditWinnerResults.length === 1 ? '' : 's' }}</span>
                                <a :href="hangfireUrl" target="_blank" class="hangfire-link hangfire-link--inline">Hangfire Dashboard &#8599;</a>
                            </div>
                            <div v-for="group in groupedResults" :key="group.creditAmount" class="result-group">
                                <button class="result-group-toggle" @click="toggleGroup(group.creditAmount)">
                                    <span class="result-group-amount">£{{ group.creditAmount.toLocaleString() }} credit</span>
                                    <span class="result-group-count">{{ group.winners.length }} user{{ group.winners.length === 1 ? '' : 's' }}</span>
                                    <span class="result-group-chevron" :class="{ 'is-open': isGroupOpen(group.creditAmount) }">&#8964;</span>
                                </button>
                                <div v-if="isGroupOpen(group.creditAmount)" class="result-group-body">
                                    <table class="data-table">
                                        <thead>
                                            <tr>
                                                <th class="col-num">#</th>
                                                <th>User ID</th>
                                                <th>Ticket Number</th>
                                                <th class="col-action"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(winner, index) in group.winners" :key="index">
                                                <td class="col-num muted">{{ index + 1 }}</td>
                                                <td><span class="user-id">{{ winner.userId }}</span></td>
                                                <td><span class="ticket">{{ winner.ticketNumber }}</span></td>
                                                <td class="col-action">
                                                    <a :href="`/users/${winner.userId}`"
                                                       target="_blank"
                                                       class="btn-view-user"
                                                       title="View user">&#8599;</a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- Entry Winners -->
                <div v-if="selectedOption === 'entry-winners'" class="content-card">
                    <div class="content-card-header">
                        <span class="card-title">Entry Winners</span>
                        <span class="card-badge">WSP{{ selectedCompetitionId }}</span>
                    </div>
                    <div class="content-card-body">

                        <!-- Step 3a: Select target competition -->
                        <div class="inner-step" :class="{ 'inner-step--compact': targetCompetitionId }">
                            <div class="inner-step-header">
                                <span class="step-badge step-badge--sm">3</span>
                                <span class="step-label">Select the live competition to add entries to</span>
                                <span v-if="targetCompetitionId" class="step-summary">{{ targetCompetitionLabel }}</span>
                                <button v-if="targetCompetitionId" class="step-change" @click="targetCompetitionId = null">Change</button>
                            </div>
                            <div v-if="!targetCompetitionId" class="inner-step-body">
                                <div v-if="loadingTargetCompetitions" class="loading-row">
                                    <span class="loading-spinner"></span>
                                    Loading live competitions...
                                </div>
                                <select v-else class="styled-select" v-model="targetCompetitionId">
                                    <option disabled :value="null">Choose a live competition...</option>
                                    <option v-for="comp in targetActiveCompetitions"
                                            :key="comp.competitionId"
                                            :value="comp.competitionId">
                                        WSP{{ comp.competitionId }} &mdash; {{ comp.competitionTitle }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Entry form (shown once target competition selected) -->
                        <div v-if="targetCompetitionId">

                            <div class="input-row">
                                <div class="field-group">
                                    <label class="field-label" for="entryCount">Number of Entries</label>
                                    <input id="entryCount"
                                           class="field-input"
                                           type="number"
                                           min="1"
                                           v-model.number="newEntryCount"
                                           placeholder="e.g. 5" />
                                </div>
                                <div class="field-group">
                                    <label class="field-label" for="entryUsers">Number of Users</label>
                                    <input id="entryUsers"
                                           class="field-input"
                                           type="number"
                                           min="1"
                                           v-model.number="newEntryUsers"
                                           placeholder="e.g. 3" />
                                </div>
                                <button class="btn-add"
                                        @click="addEntryWinnerEntry"
                                        :disabled="!newEntryCount || !newEntryUsers">
                                    + Add to List
                                </button>
                            </div>

                            <div v-if="entryWinnerEntries.length" class="table-wrap">
                                <table class="data-table">
                                    <thead>
                                        <tr>
                                            <th class="col-num">#</th>
                                            <th>Number of Entries</th>
                                            <th>Number of Users</th>
                                            <th class="col-action"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(entry, index) in entryWinnerEntries" :key="index">
                                            <td class="col-num muted">{{ index + 1 }}</td>
                                            <td><span class="currency">{{ entry.numberOfEntries }}</span></td>
                                            <td>{{ entry.numberOfUsers }}</td>
                                            <td class="col-action">
                                                <button class="btn-remove" @click="removeEntryWinnerEntry(index)" title="Remove">&#x2715;</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="table-footer">
                                    <span class="table-summary">
                                        {{ entryWinnerEntries.length }} {{ entryWinnerEntries.length === 1 ? 'entry' : 'entries' }}
                                    </span>
                                    <button class="btn-primary" @click="submitEntryWinners" :disabled="isSubmittingEntryWinners">
                                        <span v-if="!isSubmittingEntryWinners">Add Entries</span>
                                        <span v-else class="btn-spinner"></span>
                                    </button>
                                </div>
                            </div>

                            <p v-if="!entryWinnerEntries.length && !entryWinnerResults.length" class="empty-hint">Use the form above to add entry winner rows.</p>

                            <!-- Results panel -->
                            <div v-if="entryWinnerResults.length" class="results-card" style="margin-top:1.25rem;">
                                <div class="results-header">
                                    <span class="results-title">Selected Users</span>
                                    <span class="results-count" style="margin-right:auto;">{{ entryWinnerResults.length }} user{{ entryWinnerResults.length === 1 ? '' : 's' }}</span>
                                    <a :href="hangfireUrl" target="_blank" class="hangfire-link hangfire-link--inline">Hangfire Dashboard &#8599;</a>
                                </div>
                                <div v-for="group in groupedEntryResults" :key="group.numberOfEntries" class="result-group">
                                    <button class="result-group-toggle" @click="toggleEntryGroup(group.numberOfEntries)">
                                        <span class="result-group-amount">{{ group.numberOfEntries }} {{ group.numberOfEntries === 1 ? 'entry' : 'entries' }}</span>
                                        <span class="result-group-count">{{ group.winners.length }} user{{ group.winners.length === 1 ? '' : 's' }}</span>
                                        <span class="result-group-chevron" :class="{ 'is-open': isEntryGroupOpen(group.numberOfEntries) }">&#8964;</span>
                                    </button>
                                    <div v-if="isEntryGroupOpen(group.numberOfEntries)" class="result-group-body">
                                        <table class="data-table">
                                            <thead>
                                                <tr>
                                                    <th class="col-num">#</th>
                                                    <th>User ID</th>
                                                    <th class="col-action"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(winner, index) in group.winners" :key="index">
                                                    <td class="col-num muted">{{ index + 1 }}</td>
                                                    <td><span class="user-id">{{ winner.userId }}</span></td>
                                                    <td class="col-action">
                                                        <a :href="`/users/${winner.userId}`"
                                                           target="_blank"
                                                           class="btn-view-user"
                                                           title="View user">&#8599;</a>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <p v-if="!targetCompetitionId" class="empty-hint" style="margin-top:0.75rem;">Select a live competition above to continue.</p>

                    </div>
                </div>

                <!-- Auto Entries -->
                <div v-if="selectedOption === 'auto-entries'" class="content-card">
                    <div class="content-card-header">
                        <span class="card-title">Add Rewards Pass Entry</span>
                        <span class="card-badge">WSP{{ selectedCompetitionId }}</span>
                    </div>
                    <div class="content-card-body">

                        <div class="input-row">
                            <div class="field-group">
                                <label class="field-label" for="entriesPerMember">Entries per Rewards Pass Member</label>
                                <input id="entriesPerMember"
                                       class="field-input"
                                       type="number"
                                       min="1"
                                       v-model.number="entriesPerMember"
                                       placeholder="e.g. 5" />
                            </div>
                            <button class="btn-add"
                                    @click="submitAutoEntries"
                                    :disabled="!entriesPerMember || isSubmittingAutoEntries || autoEntryResults.length > 0">
                                <span v-if="!isSubmittingAutoEntries">Add Entries</span>
                                <span v-else class="btn-spinner"></span>
                            </button>
                        </div>

                        <p v-if="!autoEntryResults.length" class="empty-hint">Enter the number of entries and click Add Entries.</p>

                        <!-- Results panel -->
                        <div v-if="autoEntryResults.length" class="results-card">
                            <div class="results-header">
                                <span class="results-title">Entries Added</span>
                                <span class="results-count" style="margin-right:auto;">{{ autoEntryResults.length }} user{{ autoEntryResults.length === 1 ? '' : 's' }}</span>
                                <a :href="hangfireUrl" target="_blank" class="hangfire-link hangfire-link--inline">Hangfire Dashboard &#8599;</a>
                            </div>
                            <div class="ae-results-body">
                                <table class="data-table">
                                    <thead>
                                        <tr>
                                            <th class="col-num">#</th>
                                            <th>User ID</th>
                                            <th class="col-action"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(entry, index) in autoEntryResults" :key="index">
                                            <td class="col-num muted">{{ index + 1 }}</td>
                                            <td><span class="user-id">{{ entry.userId }}</span></td>
                                            <td class="col-action">
                                                <a :href="`/users/${entry.userId}`"
                                                   target="_blank"
                                                   class="btn-view-user"
                                                   title="View user">&#8599;</a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                </div>

            </div>

        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, ref, computed, watch } from 'vue';
    import competitionService from '../services/competition-service';
    import creditWinnersService from '../services/credit-winners-service';
    import autoEntriesService from '../services/auto-entries-service';
    import entryWinnersService from '../services/entry-winners-service';
    import EndedCompetitionsWithoutWinnersResponse from '../responses/EndedCompetitionsWithoutWinnersResponse';
    import ActiveCompetitionsResponse from '../responses/ActiveCompetitionsResponse';
    import CreditWinnerResult from '../responses/CreditWinnerResult';
    import AutoEntryResult from '../responses/AutoEntryResult';
    import EntryWinnerResult from '../responses/EntryWinnerResult';
    import { Swal, showAlert } from '../utils/global';

    interface CreditEntry {
        creditAmount: number;
        numberOfUsers: number;
    }

    interface EntryWinnerEntry {
        numberOfEntries: number;
        numberOfUsers: number;
    }

    const OPTION_LABELS: Record<string, string> = {
        'credit-winners': 'Select Credit Winners',
        'entry-winners':  'Select Entry Winners',
        'auto-entries':   'Add Rewards Pass Entry',
    };

    export default defineComponent({
        name: 'CreditAndEntriesView',
        setup() {
            const selectedOption = ref<string | null>(null);
            const selectedCompetitionId = ref<number | null>(null);
            const loadingCompetitions = ref(false);
            const endedCompetitions = ref<EndedCompetitionsWithoutWinnersResponse[]>([]);
            const activeCompetitions = ref<ActiveCompetitionsResponse[]>([]);

            const newCreditAmount = ref<number | null>(null);
            const newNumberOfUsers = ref<number | null>(null);
            const creditEntries = ref<CreditEntry[]>([]);
            const isSubmitting = ref(false);
            const creditWinnerResults = ref<CreditWinnerResult[]>([]);

            const entriesPerMember = ref<number | null>(null);
            const isSubmittingAutoEntries = ref(false);
            const autoEntryResults = ref<AutoEntryResult[]>([]);

            const targetCompetitionId = ref<number | null>(null);
            const loadingTargetCompetitions = ref(false);
            const targetActiveCompetitions = ref<ActiveCompetitionsResponse[]>([]);

            const newEntryCount = ref<number | null>(null);
            const newEntryUsers = ref<number | null>(null);
            const entryWinnerEntries = ref<EntryWinnerEntry[]>([]);
            const isSubmittingEntryWinners = ref(false);
            const entryWinnerResults = ref<EntryWinnerResult[]>([]);
            const openEntryGroups = ref<Record<number, boolean>>({});

            const optionLabel = computed(() =>
                selectedOption.value ? OPTION_LABELS[selectedOption.value] ?? '' : ''
            );

            const openGroups = ref<Record<number, boolean>>({});

            const groupedResults = computed(() => {
                const map = new Map<number, CreditWinnerResult[]>();
                for (const w of creditWinnerResults.value) {
                    if (!map.has(w.creditAmount)) map.set(w.creditAmount, []);
                    map.get(w.creditAmount)!.push(w);
                }
                return Array.from(map.entries()).map(([creditAmount, winners]) => ({
                    creditAmount,
                    winners,
                }));
            });

            function isGroupOpen(amount: number): boolean {
                return openGroups.value[amount] !== false;
            }

            function toggleGroup(amount: number) {
                openGroups.value[amount] = !isGroupOpen(amount);
            }

            const hangfireUrl = computed(() => {
                const h = window.location.hostname;
                if (h.indexOf('localhost') >= 0) return 'https://localhost:7162/hangfire';
                if (h.indexOf('test') >= 0) return 'https://neu-r4g-app-comp-crm-ap-testing-aqhtbcduedehh5g7.northeurope-01.azurewebsites.net/hangfire';
                if (h.indexOf('staging') >= 0) return 'https://neu-r4g-app-comp-crm-ap-staging-btfag9e5cpgndebf.northeurope-01.azurewebsites.net/hangfire';
                return 'https://neu-r4g-app-comp-crm-api-ced7eqhmhcfraweh.northeurope-01.azurewebsites.net/hangfire';
            });

            const competitionLabel = computed(() => {
                if (!selectedCompetitionId.value) return '';
                if (selectedOption.value !== 'auto-entries') {
                    const c = endedCompetitions.value.find(x => x.competitionId === selectedCompetitionId.value);
                    return c ? `WSP${c.competitionId} — ${c.competitionName}` : '';
                } else {
                    const c = activeCompetitions.value.find(x => x.competitionId === selectedCompetitionId.value);
                    return c ? `WSP${c.competitionId} — ${c.competitionTitle}` : '';
                }
            });
            const targetCompetitionLabel = computed(() => {
                if (!targetCompetitionId.value) return '';
                const c = targetActiveCompetitions.value.find(x => x.competitionId === targetCompetitionId.value);
                return c ? `WSP${c.competitionId} — ${c.competitionTitle}` : '';
            });

            function resetOption() {
                selectedOption.value = null;
                selectedCompetitionId.value = null;
                creditEntries.value = [];
                creditWinnerResults.value = [];
                entriesPerMember.value = null;
                autoEntryResults.value = [];
                targetCompetitionId.value = null;
                entryWinnerEntries.value = [];
                entryWinnerResults.value = [];
            }

            function resetCompetition() {
                selectedCompetitionId.value = null;
                creditEntries.value = [];
                creditWinnerResults.value = [];
                entriesPerMember.value = null;
                autoEntryResults.value = [];
                targetCompetitionId.value = null;
                entryWinnerEntries.value = [];
                entryWinnerResults.value = [];
            }

            function addCreditEntry() {
                if (!newCreditAmount.value || !newNumberOfUsers.value) return;
                creditEntries.value.push({
                    creditAmount: newCreditAmount.value,
                    numberOfUsers: newNumberOfUsers.value,
                });
                newCreditAmount.value = null;
                newNumberOfUsers.value = null;
            }

            function removeCreditEntry(index: number) {
                creditEntries.value.splice(index, 1);
            }

            async function submitCreditWinners() {
                if (!selectedCompetitionId.value || !creditEntries.value.length) return;

                const rows = creditEntries.value
                    .map((e, i) => `<tr><td style="padding:4px 10px;color:#888;">${i + 1}</td><td style="padding:4px 10px;">£${e.creditAmount.toLocaleString()}</td><td style="padding:4px 10px;">${e.numberOfUsers}</td></tr>`)
                    .join('');

                const result = await Swal.fire({
                    icon: 'question',
                    title: 'Confirm Credit Winners',
                    html: `
                        <p style="margin-bottom:0.75rem;">You are about to add credit for <strong>WSP${selectedCompetitionId.value}</strong>.</p>
                        <table style="width:100%;border-collapse:collapse;font-size:0.88rem;text-align:left;">
                            <thead>
                                <tr style="border-bottom:2px solid #e0e0e0;">
                                    <th style="padding:4px 10px;color:#555;font-size:0.75rem;text-transform:uppercase;">#</th>
                                    <th style="padding:4px 10px;color:#555;font-size:0.75rem;text-transform:uppercase;">Credit</th>
                                    <th style="padding:4px 10px;color:#555;font-size:0.75rem;text-transform:uppercase;">Users</th>
                                </tr>
                            </thead>
                            <tbody>${rows}</tbody>
                        </table>
                        <p style="margin-top:1rem;">Do you want to continue?</p>
                    `,
                    showCancelButton: true,
                    confirmButtonText: 'Yes, add credit',
                    cancelButtonText: 'Cancel',
                    background: 'var(--clr-white)',
                    customClass: {
                        confirmButton: 'my-custom-button',
                    },
                });

                if (!result.isConfirmed) return;

                isSubmitting.value = true;
                try {
                    creditWinnerResults.value = await creditWinnersService.addCreditWinners(
                        selectedCompetitionId.value,
                        creditEntries.value
                    );
                    creditEntries.value = [];
                } catch (error: any) {
                    const message =
                        error?.response?.data?.errors?.[0] ??
                        error?.response?.data?.message ??
                        error?.message ??
                        'An unexpected error occurred.';
                    showAlert('Failed to Add Credit', message, 'error');
                } finally {
                    isSubmitting.value = false;
                }
            }

            function addEntryWinnerEntry() {
                if (!newEntryCount.value || !newEntryUsers.value) return;
                entryWinnerEntries.value.push({
                    numberOfEntries: newEntryCount.value,
                    numberOfUsers: newEntryUsers.value,
                });
                newEntryCount.value = null;
                newEntryUsers.value = null;
            }

            function removeEntryWinnerEntry(index: number) {
                entryWinnerEntries.value.splice(index, 1);
            }

            function isEntryGroupOpen(entries: number): boolean {
                return openEntryGroups.value[entries] !== false;
            }

            function toggleEntryGroup(entries: number) {
                openEntryGroups.value[entries] = !isEntryGroupOpen(entries);
            }

            const groupedEntryResults = computed(() => {
                const map = new Map<number, EntryWinnerResult[]>();
                for (const w of entryWinnerResults.value) {
                    if (!map.has(w.numberOfEntries)) map.set(w.numberOfEntries, []);
                    map.get(w.numberOfEntries)!.push(w);
                }
                return Array.from(map.entries()).map(([numberOfEntries, winners]) => ({
                    numberOfEntries,
                    winners,
                }));
            });

            async function submitEntryWinners() {
                if (!selectedCompetitionId.value || !targetCompetitionId.value || !entryWinnerEntries.value.length) return;

                const rows = entryWinnerEntries.value
                    .map((e, i) => `<tr><td style="padding:4px 10px;color:#888;">${i + 1}</td><td style="padding:4px 10px;">${e.numberOfEntries}</td><td style="padding:4px 10px;">${e.numberOfUsers}</td></tr>`)
                    .join('');

                const result = await Swal.fire({
                    icon: 'question',
                    title: 'Confirm Entry Winners',
                    html: `
                        <p style="margin-bottom:0.75rem;">Adding entries from <strong>WSP${selectedCompetitionId.value}</strong> winners into <strong>WSP${targetCompetitionId.value}</strong>.</p>
                        <table style="width:100%;border-collapse:collapse;font-size:0.88rem;text-align:left;">
                            <thead>
                                <tr style="border-bottom:2px solid #e0e0e0;">
                                    <th style="padding:4px 10px;color:#555;font-size:0.75rem;text-transform:uppercase;">#</th>
                                    <th style="padding:4px 10px;color:#555;font-size:0.75rem;text-transform:uppercase;">Entries</th>
                                    <th style="padding:4px 10px;color:#555;font-size:0.75rem;text-transform:uppercase;">Users</th>
                                </tr>
                            </thead>
                            <tbody>${rows}</tbody>
                        </table>
                        <p style="margin-top:1rem;">Do you want to continue?</p>
                    `,
                    showCancelButton: true,
                    confirmButtonText: 'Yes, add entries',
                    cancelButtonText: 'Cancel',
                    background: 'var(--clr-white)',
                    customClass: { confirmButton: 'my-custom-button' },
                });

                if (!result.isConfirmed) return;

                isSubmittingEntryWinners.value = true;
                try {
                    entryWinnerResults.value = await entryWinnersService.addEntryWinners(
                        selectedCompetitionId.value,
                        targetCompetitionId.value,
                        entryWinnerEntries.value
                    );
                    entryWinnerEntries.value = [];
                } catch (error: any) {
                    const message =
                        error?.response?.data?.errors?.[0] ??
                        error?.response?.data?.message ??
                        error?.message ??
                        'An unexpected error occurred.';
                    showAlert('Failed to Add Entry Winners', message, 'error');
                } finally {
                    isSubmittingEntryWinners.value = false;
                }
            }

            async function submitAutoEntries() {
                if (!selectedCompetitionId.value || !entriesPerMember.value) return;

                const result = await Swal.fire({
                    icon: 'question',
                    title: 'Confirm Rewards Pass Entry',
                    html: `
                        <p>You are about to add <strong>${entriesPerMember.value} ${entriesPerMember.value === 1 ? 'entry' : 'entries'}</strong> per Rewards Pass member for <strong>WSP${selectedCompetitionId.value}</strong>.</p>
                        <p style="margin-top:0.75rem;">Do you want to continue?</p>
                    `,
                    showCancelButton: true,
                    confirmButtonText: 'Yes, add entries',
                    cancelButtonText: 'Cancel',
                    background: 'var(--clr-white)',
                    customClass: { confirmButton: 'my-custom-button' },
                });

                if (!result.isConfirmed) return;

                isSubmittingAutoEntries.value = true;
                try {
                    autoEntryResults.value = await autoEntriesService.addAutoEntries(
                        selectedCompetitionId.value,
                        entriesPerMember.value
                    );
                } catch (error: any) {
                    const message =
                        error?.response?.data?.errors?.[0] ??
                        error?.response?.data?.message ??
                        error?.message ??
                        'An unexpected error occurred.';
                    showAlert('Failed to Add Rewards Pass Entry', message, 'error');
                } finally {
                    isSubmittingAutoEntries.value = false;
                }
            }

            watch(selectedOption, async (val) => {
                selectedCompetitionId.value = null;
                creditEntries.value = [];

                if (!val) return;

                loadingCompetitions.value = true;

                if (val === 'credit-winners' || val === 'entry-winners') {
                    const all = await competitionService.getEndedCompetitionsWithoutWinners() ?? [];
                    endedCompetitions.value = all
                        .sort((a, b) => b.competitionId - a.competitionId)
                        .slice(0, 6);
                } else if (val === 'auto-entries') {
                    activeCompetitions.value = await competitionService.getActiveCompetitions() ?? [];
                }

                loadingCompetitions.value = false;
            });

            watch(selectedCompetitionId, async (val) => {
                targetCompetitionId.value = null;
                if (val && selectedOption.value === 'entry-winners') {
                    loadingTargetCompetitions.value = true;
                    targetActiveCompetitions.value = await competitionService.getActiveCompetitions() ?? [];
                    loadingTargetCompetitions.value = false;
                }
            });

            return {
                selectedOption,
                selectedCompetitionId,
                loadingCompetitions,
                endedCompetitions,
                activeCompetitions,
                newCreditAmount,
                newNumberOfUsers,
                creditEntries,
                isSubmitting,
                creditWinnerResults,
                groupedResults,
                isGroupOpen,
                toggleGroup,
                entriesPerMember,
                isSubmittingAutoEntries,
                autoEntryResults,
                submitAutoEntries,
                targetCompetitionId,
                loadingTargetCompetitions,
                targetActiveCompetitions,
                targetCompetitionLabel,
                newEntryCount,
                newEntryUsers,
                entryWinnerEntries,
                isSubmittingEntryWinners,
                entryWinnerResults,
                groupedEntryResults,
                isEntryGroupOpen,
                toggleEntryGroup,
                addEntryWinnerEntry,
                removeEntryWinnerEntry,
                submitEntryWinners,
                optionLabel,
                competitionLabel,
                hangfireUrl,
                resetOption,
                resetCompetition,
                addCreditEntry,
                removeCreditEntry,
                submitCreditWinners,
            };
        },
    });
</script>

<style scoped>

    .page-container {
        padding: 2rem 2.5rem;
        max-width: 860px;
    }

    /* ── Page header ─────────────────────────────────────── */
    .page-header {
        margin-bottom: 2rem;
        padding-bottom: 1.25rem;
        border-bottom: 2px solid var(--clr-primary);
    }

    .page-title {
        font-size: 1.6rem;
        font-weight: 700;
        color: var(--clr-primary);
        margin-bottom: 0.3rem;
    }

    .page-subtitle {
        font-size: 0.9rem;
        color: #666;
    }

    /* ── Step cards ──────────────────────────────────────── */
    .step-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        margin-bottom: 1.25rem;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .step-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1.25rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e0e0e0;
    }

    .step-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background: var(--clr-primary);
        color: #fff;
        border-radius: 50%;
        font-size: 0.78rem;
        font-weight: 700;
        flex-shrink: 0;
    }

    .step-label {
        font-size: 0.9rem;
        font-weight: 600;
        color: #333;
    }

    .step-body {
        padding: 1rem 1.25rem;
    }

    .step-card--compact .step-header {
        background: #fff;
        border-bottom: none;
        padding: 0.55rem 1.25rem;
    }

    .step-card--compact .step-badge {
        background: #a8d5bf;
        width: 20px;
        height: 20px;
        font-size: 0.7rem;
    }

    .step-card--compact .step-label {
        font-weight: 400;
        color: #888;
        font-size: 0.82rem;
    }

    .step-summary {
        margin-left: 0.5rem;
        font-size: 0.88rem;
        font-weight: 600;
        color: #333;
        flex: 1;
    }

    .step-change {
        margin-left: auto;
        background: none;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 0.78rem;
        color: #666;
        padding: 0.2rem 0.65rem;
        cursor: pointer;
        transition: border-color 0.15s, color 0.15s;
    }

    .step-change:hover {
        border-color: var(--clr-primary);
        color: var(--clr-primary);
    }

    .inner-step {
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        overflow: hidden;
        margin-bottom: 0.75rem;
    }

    .inner-step-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.65rem 1rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e0e0e0;
    }

    .inner-step--compact .inner-step-header {
        background: #fff;
        border-bottom: none;
        padding: 0.5rem 1rem;
    }

    .inner-step--compact .step-badge--sm {
        background: #a8d5bf;
    }

    .inner-step--compact .step-label {
        font-weight: 400;
        color: #888;
        font-size: 0.82rem;
    }

    .step-badge--sm {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        background: var(--clr-primary);
        color: #fff;
        border-radius: 50%;
        font-size: 0.7rem;
        font-weight: 700;
        flex-shrink: 0;
    }

    .inner-step-body {
        padding: 0.85rem 1rem;
    }

    /* ── Selects ─────────────────────────────────────────── */
    .styled-select {
        width: 100%;
        max-width: 420px;
        padding: 0.55rem 0.85rem;
        border: 1px solid #ccc;
        border-radius: 5px;
        font-size: 0.9rem;
        background: #fff;
        color: #333;
        appearance: auto;
    }

    .styled-select:focus {
        outline: none;
        border-color: var(--clr-primary);
        box-shadow: 0 0 0 3px rgba(0, 123, 160, 0.12);
    }

    /* ── Loading ─────────────────────────────────────────── */
    .loading-row {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        font-size: 0.88rem;
        color: #666;
        padding: 0.25rem 0;
    }

    .loading-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid #ccc;
        border-top-color: var(--clr-primary);
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }

    @keyframes spin { to { transform: rotate(360deg); } }

    /* ── Content card ────────────────────────────────────── */
    .content-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        margin-bottom: 1.25rem;
    }

    .content-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1.25rem;
        background: var(--clr-primary);
    }

    .card-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: #fff;
    }

    .card-badge {
        font-size: 0.78rem;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.15);
        padding: 0.2rem 0.6rem;
        border-radius: 12px;
        letter-spacing: 0.03em;
    }

    .content-card-body {
        padding: 1.25rem;
    }

    /* ── Input row ───────────────────────────────────────── */
    .input-row {
        display: flex;
        align-items: flex-end;
        gap: 1rem;
        flex-wrap: wrap;
        padding-bottom: 1.25rem;
        border-bottom: 1px solid #eee;
        margin-bottom: 1.25rem;
    }

    .field-group {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        flex: 1;
        min-width: 140px;
    }

    .field-label {
        font-size: 0.78rem;
        font-weight: 700;
        color: #555;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .field-input {
        padding: 0.55rem 0.75rem;
        border: 1px solid #ccc;
        border-radius: 5px;
        font-size: 0.9rem;
        width: 100%;
        transition: border-color 0.15s, box-shadow 0.15s;
    }

    .field-input:focus {
        outline: none;
        border-color: var(--clr-primary);
        box-shadow: 0 0 0 3px rgba(0, 123, 160, 0.12);
    }

    .btn-add {
        padding: 0.55rem 1.3rem;
        background: var(--clr-primary);
        color: #fff;
        border: none;
        border-radius: 5px;
        font-size: 0.88rem;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
        align-self: flex-end;
        transition: opacity 0.15s;
    }

    .btn-add:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .btn-add:not(:disabled):hover {
        opacity: 0.85;
    }

    /* ── Table ───────────────────────────────────────────── */
    .table-wrap {
        overflow: hidden;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.88rem;
    }

    .data-table th {
        text-align: left;
        padding: 0.55rem 0.85rem;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #555;
        border-bottom: 2px solid #e0e0e0;
    }

    .data-table td {
        padding: 0.6rem 0.85rem;
        border-bottom: 1px solid #f0f0f0;
        color: #333;
    }

    .data-table tbody tr:last-child td {
        border-bottom: none;
    }

    .data-table tbody tr:hover td {
        background: #f9fbfc;
    }

    .col-num  { width: 48px; }
    .col-action { width: 52px; text-align: center; }
    .muted { color: #aaa; font-size: 0.82rem; }

    .currency {
        font-weight: 600;
        color: var(--clr-primary);
    }

    .btn-remove {
        background: none;
        border: none;
        color: #bbb;
        cursor: pointer;
        font-size: 0.8rem;
        padding: 0.25rem 0.45rem;
        border-radius: 4px;
        transition: color 0.15s, background 0.15s;
    }

    .btn-remove:hover {
        color: #d9534f;
        background: #fff0ee;
    }

    /* ── Table footer ────────────────────────────────────── */
    .table-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.85rem 0.85rem 0;
        border-top: 1px solid #eee;
        margin-top: 0.25rem;
    }

    .table-summary {
        font-size: 0.82rem;
        color: #888;
    }

    .btn-primary {
        padding: 0.55rem 1.6rem;
        background: var(--clr-primary);
        color: #fff;
        border: none;
        border-radius: 5px;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.15s;
    }

    .btn-primary:hover {
        opacity: 0.85;
    }

    .btn-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255,255,255,0.4);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
        vertical-align: middle;
    }

    /* ── Results panel ───────────────────────────────────── */
    .results-card {
        margin-top: 1.5rem;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
    }

    .result-group {
        border-top: 1px solid #e8e8e8;
    }

    .result-group:first-of-type {
        border-top: none;
    }

    .result-group-toggle {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        width: 100%;
        padding: 0.65rem 1rem;
        background: #fafafa;
        border: none;
        cursor: pointer;
        text-align: left;
        transition: background 0.15s;
    }

    .result-group-toggle:hover {
        background: #f0f0f0;
    }

    .result-group-amount {
        font-size: 0.88rem;
        font-weight: 700;
        color: var(--clr-primary);
    }

    .result-group-count {
        font-size: 0.78rem;
        color: #888;
        background: #ececec;
        padding: 0.15rem 0.5rem;
        border-radius: 10px;
    }

    .result-group-chevron {
        margin-left: auto;
        font-size: 1.1rem;
        color: #aaa;
        transition: transform 0.2s;
        display: inline-block;
        line-height: 1;
    }

    .result-group-chevron.is-open {
        transform: rotate(180deg);
    }

    .result-group-body {
        max-height: 220px;
        overflow-y: auto;
        border-top: 1px solid #eee;
    }

    .result-group-body .data-table th,
    .result-group-body .data-table td {
        padding: 0.45rem 0.85rem;
    }


    .results-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.65rem 1rem;
        background: #f0faf4;
        border-bottom: 1px solid #c8ecd6;
    }

    .results-title {
        font-size: 0.88rem;
        font-weight: 700;
        color: #2a7a4e;
        margin-right: 0.5rem;
    }

    .results-count {
        font-size: 0.78rem;
        color: #2a7a4e;
        background: #c8ecd6;
        padding: 0.15rem 0.55rem;
        border-radius: 10px;
        font-weight: 600;
    }

    .user-id {
        font-weight: 600;
        color: #333;
    }

    .ticket {
        font-family: monospace;
        font-size: 0.85rem;
        color: #555;
    }

    .btn-view-user {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        background: var(--clr-primary);
        color: #fff;
        border-radius: 4px;
        text-decoration: none;
        font-size: 0.85rem;
        font-weight: 700;
        transition: opacity 0.15s;
    }

    .btn-view-user:hover {
        opacity: 0.8;
    }

    .hangfire-link {
        font-size: 0.82rem;
        font-weight: 600;
        color: var(--clr-primary);
        text-decoration: none;
    }

    .hangfire-link:hover {
        text-decoration: underline;
    }

    .hangfire-link--inline {
        margin-left: auto;
        color: #2a7a4e;
        font-size: 0.78rem;
    }

    .ae-results-body {
        max-height: 300px;
        overflow-y: auto;
    }

    /* ── Empty hint ──────────────────────────────────────── */
    .empty-hint {
        font-size: 0.88rem;
        color: #999;
        font-style: italic;
        margin: 0;
    }

</style>



